import { AsyncPipe } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, ReactiveFormsModule } from "@angular/forms";
import { IBasicVaccinationModel } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { RequiredVaccinationsResultEntry, StaticDataModel, StaticDataType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType } from "@ngx-formly/core";
import { first, groupBy, isEmpty, isEqual } from "lodash";
import { FloatLabel } from "primeng/floatlabel";
import { Message } from "primeng/message";
import { Select } from "primeng/select";
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from "rxjs";
import { CurrentCollectionService } from "../../features/candidates/current-collection.service";
import { OrganizationsService } from "../../features/organizations/organizations.service";
import { RequiredVaccinationsService } from "../../features/required-vaccinations/required-vaccinations.service";
import { NewUiFormElementMapModule } from "../../shared/from-element-map/new-ui-form-element-map.module";
import { NewUiDropdownSortOnTopDirective } from "../../shared/primeng/dropdown-sort-selected-value-on-top/new-ui-dropdown-sort-on-top.directive";
import { SettingsService } from "../../shared/services/settings.service";
import { StaticDataService } from "../../shared/static-data/static-data.service";
import { TestIdDirective } from "../../shared/test-id/test-id.directive";

interface OrganizationOption {
  label: string;
  value: string;
  code?: string;
}

@Component({
  selector: "new-ui-formly-vaccination-information",
  templateUrl: "./new-ui-formly-vaccination-information.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    FloatLabel,
    Select,
    NewUiDropdownSortOnTopDirective,
    Message,
    NewUiFormElementMapModule,
    TestIdDirective,
  ],
})
export class NewUiFormlyVaccinationInformationComponent extends FieldType implements AfterViewInit {
  private readonly lang = this.transloco.getActiveLang();
  private readonly cacheKey = "vaccination-information-source-organization";

  sourceOrganizationControlSwitch = new FormControl<string>(undefined);

  organizationOptions$ = new BehaviorSubject<OrganizationOption[]>([]);
  hasOrganizationOptions$ = this.organizationOptions$.pipe(map((x) => !isEmpty(x)));

  vaccineStaticData$ = this.staticDataService.getStaticData(StaticDataType.Vaccination, this.lang);
  vaccineFunctionStaticData$ = this.staticDataService.getStaticData(StaticDataType.VaccineFunction, this.lang);

  messages: string[] = [];
  showSourceOrganizationControlSwitch = false;

  constructor(
    private readonly settings: SettingsService,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly requiredVaccinesService: RequiredVaccinationsService,
    private readonly currentCollectionService: CurrentCollectionService,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly organizationService: OrganizationsService
  ) {
    super();
  }

  ngAfterViewInit(): void {
    // In candidate multi-edit the `vaccinations` list is not part of the form (see `handleMultiEditLists`), so there
    // is nothing to compute the missing vaccinations from.
    const vaccinationsControl = this.field?.parent?.fieldGroup?.find((x) => x.key === "vaccinations")?.formControl;
    if (!vaccinationsControl) return;

    this.getOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((x) => this.organizationOptions$.next(x));

    this.organizationOptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options) => {
      const cached = localStorage.getItem(this.cacheKey);
      if (options.some((option) => option.value === cached)) this.sourceOrganizationControlSwitch.setValue(cached);
      else this.sourceOrganizationControlSwitch.setValue(first(options)?.value);
    });

    this.showSourceOrganizationControlSwitch =
      !this.settings.isCandidate && this.settings.organizationId != this.formState.organizationId;

    combineLatest([
      this.getRequiredVaccinations$(),
      this.getFullVaccines$(vaccinationsControl),
      this.vaccineStaticData$,
      this.vaccineFunctionStaticData$,
    ])
      .pipe(
        map(([required, current, vaccineStaticData, vaccineFunctionStaticData]) =>
          this.getMissingVaccines(required, current, vaccineStaticData, vaccineFunctionStaticData)
        ),
        distinctUntilChanged(isEqual),
        map((x) => this.getWarnings(x))
      )
      .subscribe((messages) => {
        this.messages = messages;
        this.changeDetector.detectChanges();
      });

    this.sourceOrganizationControlSwitch.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((vaccinationSource) => this.cacheVaccinationSource(vaccinationSource));
  }

  private getFullVaccines$(formControl: AbstractControl): Observable<string[]> {
    return formControl.valueChanges.pipe(
      startWith(formControl.value),
      map(
        (entry: IBasicVaccinationModel[]) =>
          entry?.filter((x) => x.vaccinationStatus === "FULL")?.flatMap((v) => v.vaccinations) ?? []
      )
    );
  }

  private getRequiredVaccinations$(): Observable<RequiredVaccinationsResultEntry[]> {
    return this.getRequiredVaccinationsSourceOrgId$().pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(50),
      switchMap((x) => this.loadRequiredVaccinations$(x))
    );
  }

  private getRequiredVaccinationsSourceOrgId$(): Observable<string> {
    if (this.formState.organizationId == this.settings.organizationId) return of(this.settings.organizationId);

    return this.sourceOrganizationControlSwitch.valueChanges.pipe(
      startWith(this.sourceOrganizationControlSwitch.value)
    );
  }

  private loadRequiredVaccinations$(overviewOrganizationId: string): Observable<RequiredVaccinationsResultEntry[]> {
    if (!this.formState.organizationId || !this.formState.candidateId) return of([]);
    return this.requiredVaccinesService.get({
      overviewOrganizationId: overviewOrganizationId ?? this.formState.organizationId,
      candidateOrganizationId: this.formState.organizationId,
      candidateId: this.formState.candidateId,
    });
  }

  private getMissingVaccines(
    required: RequiredVaccinationsResultEntry[],
    current: string[],
    vaccineStaticData: StaticDataModel[],
    vaccineFunctionStaticData: StaticDataModel[]
  ): { type: string; missing: string[] }[] {
    const grouped: Record<string, RequiredVaccinationsResultEntry[]> = groupBy(required, (v) => v.vaccineFunction);

    return Object.entries(grouped)
      .map(([key, value]) => ({
        type: vaccineFunctionStaticData.find((v) => v.value === key)?.label,
        missing: value
          .flatMap((v) => v.vaccines)
          .filter((v) => !current.includes(v))
          .map((x) => vaccineStaticData.find((v) => v.value === x)?.label),
      }))
      .filter((x) => !isEmpty(x.missing));
  }

  private getWarnings(missing: { type: string; missing: string[] }[]): string[] {
    return isEmpty(missing)
      ? []
      : missing.map(
          (x) => `${translateSafely("vaccinations.missing.title", { type: x.type })}: ${x.missing.join(", ")}`
        );
  }

  private getOptions(): Observable<OrganizationOption[]> {
    const ownerOption = {
      value: this.settings.organizationId,
      label: this.settings.organizationName,
      code: this.settings.organizationCode,
    };

    if (this.isCandidateOwnerOrg()) return of([ownerOption]);

    const sharerOptions = this.getSharerOptions();

    return sharerOptions.pipe(map((x) => (this.settings.isLicensed ? [ownerOption, ...x] : x)));
  }

  private getSharerOptions(): Observable<OrganizationOption[]> {
    if (this.currentCollectionService.organizationId)
      return of([
        {
          value: this.currentCollectionService.organizationId,
          label: this.currentCollectionService.organizationName,
        },
      ]);

    if (!this.formState.candidateId || !this.formState.organizationId) return of([]);

    return this.organizationService
      .getOrganizationsSharingTabToCandidate(this.formState.candidateId, this.formState.organizationId, "vaccinations")
      .pipe(map((organizations) => organizations.map((org) => ({ value: org.id, label: org.name, code: org.code }))));
  }

  private isCandidateOwnerOrg(): boolean {
    return this.settings.organizationId === this.formState.organizationId;
  }

  private cacheVaccinationSource(vaccinationSource: string): void {
    if (!isEmpty(vaccinationSource)) localStorage.setItem(this.cacheKey, vaccinationSource);
  }
}
